import { useCallback, useEffect, useRef, useState } from 'react';
import { useNotify } from 'react-admin';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { dateTimeNoSeconds } from '../../shared/dateTime';
import { SectionCard } from '../../shared/Panel';
import { useStillMounted } from '../../shared/recordScoped';
import { useFeature } from '../featureFlags/FeatureGate';
import { MessageDraft } from './MessageDraft';
import {
  FEATURE_CLIENT_QUESTIONS,
  MIN_REASON,
  answerClientQuestion,
  cancelClientQuestion,
  createClientQuestion,
  fetchClientQuestions,
  markClientQuestionSent,
  saveClientQuestion,
  toErrorMessage,
  type ClientQuestion,
} from './clientCommunicationApi';

/** Pola nagłówka prezlecenia, o które można zapytać — klucze z API (dane). */
export const ASKABLE_FIELDS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'customerOrderNumber', label: 'numer zamówienia klienta' },
  { key: 'loadingDate', label: 'data załadunku' },
  { key: 'unloadingDate', label: 'data rozładunku' },
  { key: 'loadingCity', label: 'miasto załadunku' },
  { key: 'unloadingCity', label: 'miasto rozładunku' },
  { key: 'loadingPostalCode', label: 'kod pocztowy załadunku' },
  { key: 'unloadingPostalCode', label: 'kod pocztowy rozładunku' },
  { key: 'requiredVehicleType', label: 'wymagany typ pojazdu' },
  { key: 'cargo', label: 'ładunek' },
  { key: 'weight', label: 'waga' },
  { key: 'pallets', label: 'palety' },
  { key: 'price', label: 'cena' },
];

const STATE_COLOR: Record<string, 'warning' | 'info' | 'success' | 'default'> = {
  draft: 'warning',
  sent_manually: 'info',
  answered: 'success',
  cancelled: 'default',
};

/** Jedno pytanie: szkic → wysłane ręcznie → odpowiedź; odwołanie z powodem. */
const QuestionCard = ({
  q,
  canWrite,
  onChanged,
}: {
  q: ClientQuestion;
  canWrite: boolean;
  onChanged: () => void;
}) => {
  const notify = useNotify();
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [reason, setReason] = useState('');
  const open = q.state === 'draft' || q.state === 'sent_manually';

  const run = async (action: () => Promise<unknown>, message: string) => {
    setBusy(true);
    try {
      await action();
      notify(message, { type: 'success' });
      onChanged();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }} data-testid={`client-question-${q.id}`} data-state={q.state}>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
        <Chip
          size="small"
          color={q.overdue ? 'error' : (STATE_COLOR[q.state] ?? 'default')}
          label={q.overdue ? 'szkic po terminie — przypomnienie' : q.stateLabel}
        />
        <Typography variant="caption" color="text.secondary">
          o: {q.fieldLabels.join(', ')} · {q.createdByName ?? '—'}, {dateTimeNoSeconds(q.createdAt)}
          {q.sentAt ? ` · wysłane ręcznie: ${q.sentByName ?? '—'}, ${dateTimeNoSeconds(q.sentAt)}` : ''}
        </Typography>
      </Stack>
      {q.state === 'draft' || q.state === 'sent_manually' ? (
        <MessageDraft
          subject={q.subject}
          body={q.body}
          recipient={q.recipient}
          editable={q.state === 'draft' && canWrite}
          busy={busy}
          testIdPrefix={`client-question-${q.id}`}
          onSave={(v) => run(() => saveClientQuestion(q.id, { subject: v.subject, body: v.body, recipient: v.recipient }), 'Zapisano poprawki')}
          onSent={() => run(() => markClientQuestionSent(q.id), 'Pytanie oznaczone jako wysłane — czeka na odpowiedź klienta')}
        />
      ) : null}
      {q.state === 'answered' ? (
        <Alert severity="success" data-testid={`client-question-answer-${q.id}`}>
          Odpowiedź klienta ({q.answeredByName ?? '—'}, {dateTimeNoSeconds(q.answeredAt)}): {q.answerText}
        </Alert>
      ) : null}
      {q.state === 'cancelled' ? <Alert severity="info">Odwołane: {q.cancelReason}</Alert> : null}
      {q.state === 'sent_manually' && canWrite ? (
        <Stack spacing={1} sx={{ mt: 1 }}>
          <TextField
            size="small"
            label="Odpowiedź klienta"
            value={answer}
            multiline
            minRows={2}
            onChange={(e) => setAnswer(e.target.value)}
            helperText="Przepisz, co odpowiedział klient; dane prezlecenia popraw przez „Popraw dane” ze źródłem ustalenia."
            slotProps={{ htmlInput: { maxLength: 2000, 'data-testid': `client-question-answer-text-${q.id}` } }}
          />
          <Box>
            <Button
              size="small"
              variant="contained"
              disabled={busy || answer.trim().length < 3}
              onClick={() => void run(() => answerClientQuestion(q.id, answer), 'Zapisano odpowiedź klienta')}
              data-testid={`client-question-answer-save-${q.id}`}
            >
              Wpisz odpowiedź
            </Button>
          </Box>
        </Stack>
      ) : null}
      {open && canWrite ? (
        cancelling ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Powód odwołania"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              helperText={`co najmniej ${MIN_REASON} znaków`}
              slotProps={{ htmlInput: { maxLength: 500 } }}
            />
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                size="small"
                disabled={busy || reason.trim().length < MIN_REASON}
                onClick={() => void run(() => cancelClientQuestion(q.id, reason), 'Pytanie odwołane')}
              >
                Odwołaj pytanie
              </Button>
              <Button size="small" onClick={() => setCancelling(false)}>
                Wróć
              </Button>
            </Box>
          </Stack>
        ) : (
          <Button size="small" sx={{ mt: 1 }} onClick={() => setCancelling(true)}>
            Odwołaj
          </Button>
        )
      ) : null}
    </Box>
  );
};

/**
 * Pytanie do klienta przy prezleceniu (makieta: okno zlecenia CS, „Przygotuj
 * pytanie do klienta”, A15 w wersji zgodnej; prace 26.10.2026, pakiet P1;
 * przełącznik `clientQuestions`, S-26.10-16): CS zaznacza brakujące pola,
 * system przygotowuje szkic, CS wysyła ze swojej poczty i oznacza „Wysłane
 * ręcznie”; potem pytanie czeka na odpowiedź, którą CS wpisuje.
 */
export const ClientQuestionsPanel = ({ preOrderId }: { preOrderId: number }) => (
  // Stan panelu należy do jednego prezlecenia (poprawka integracji 26.10.2026): karta prezlecenia przy
  // przejściu na inne prezlecenie nie tworzy się od nowa, więc bez klucza panel pokazywał najpierw
  // pytania i przycisk poprzedniego rekordu, a po wczytaniu bieżącego nadpisywał otwarty formularz.
  <ClientQuestionsSection key={preOrderId} preOrderId={preOrderId} />
);

const ClientQuestionsSection = ({ preOrderId }: { preOrderId: number }) => {
  const notify = useNotify();
  const stillMounted = useStillMounted();
  const on = useFeature(FEATURE_CLIENT_QUESTIONS);
  const [data, setData] = useState<Awaited<ReturnType<typeof fetchClientQuestions>> | null>(null);
  const [preparing, setPreparing] = useState(false);
  const [picked, setPicked] = useState<string[]>([]);
  const [recipient, setRecipient] = useState('');
  const [busy, setBusy] = useState(false);
  // Otwarty formularz należy do operatora: ponowne wczytanie (drugie zapytanie po wejściu na kartę,
  // odświeżenie po akcji) odświeża listę pytań, ale nie nadpisuje zaznaczonych pól ani adresata.
  const formOpen = useRef(false);
  formOpen.current = preparing;

  const load = useCallback(() => {
    if (!on) return;
    fetchClientQuestions(preOrderId)
      .then((d) => {
        if (!stillMounted()) return;
        setData(d);
        if (formOpen.current) return;
        setPicked(d.missingFields.map((f) => f.key).filter((k) => ASKABLE_FIELDS.some((a) => a.key === k)));
        setRecipient(d.suggestedRecipient ?? '');
      })
      .catch(() => undefined);
  }, [on, preOrderId, stillMounted]);
  useEffect(load, [load]);

  if (!on || !data) return null;
  const sent = data.items.some((q) => q.state === 'sent_manually');
  const hasDraft = data.items.some((q) => q.state === 'draft');

  const prepare = async () => {
    setBusy(true);
    try {
      await createClientQuestion(preOrderId, { fields: picked, ...(recipient.trim() ? { recipient: recipient.trim() } : {}) });
      notify('Przygotowano szkic pytania do klienta', { type: 'success' });
      setPreparing(false);
      load();
    } catch (e) {
      notify(toErrorMessage(e), { type: 'error' });
    } finally {
      if (stillMounted()) setBusy(false);
    }
  };

  return (
    <SectionCard title="Pytanie do klienta">
      <Box data-testid="client-questions">
        {sent ? (
          <Chip size="small" color="info" label="Pytanie do klienta wysłane — czeka na odpowiedź" data-testid="client-question-sent-marker" sx={{ mb: 1 }} />
        ) : null}
        <Stack spacing={1}>
          {data.items.map((q) => (
            <QuestionCard key={q.id} q={q} canWrite={data.canWrite} onChanged={load} />
          ))}
        </Stack>
        {data.items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Brak pytań do klienta.
          </Typography>
        ) : null}
        {data.canWrite && !hasDraft ? (
          preparing ? (
            <Stack spacing={1} sx={{ mt: 1 }} data-testid="client-question-form">
              <Typography variant="body2">O co pytasz? Zaznaczone są braki prezlecenia.</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))' }}>
                {ASKABLE_FIELDS.map((f) => (
                  <FormControlLabel
                    key={f.key}
                    data-testid={`client-question-field-${f.key}`}
                    control={
                      <Checkbox
                        size="small"
                        checked={picked.includes(f.key)}
                        onChange={(e) =>
                          setPicked((list) => (e.target.checked ? [...list, f.key] : list.filter((k) => k !== f.key)))
                        }
                      />
                    }
                    label={f.label}
                  />
                ))}
              </Box>
              <TextField
                size="small"
                label="Adresat"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                helperText={data.suggestedRecipient ? 'z kartoteki kontrahenta' : 'kartoteka nie ma adresu — wpisz go albo dopisz w poczcie'}
                slotProps={{ htmlInput: { maxLength: 320, 'data-testid': 'client-question-recipient' } }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" variant="contained" disabled={busy || picked.length === 0} onClick={() => void prepare()} data-testid="client-question-create">
                  Przygotuj szkic
                </Button>
                <Button size="small" onClick={() => setPreparing(false)}>
                  Wróć
                </Button>
              </Box>
            </Stack>
          ) : (
            // Kontrakt `data-testid` paczki: „Przygotuj pytanie do klienta” w oknie zlecenia CS.
            <Button size="small" sx={{ mt: 1 }} onClick={() => setPreparing(true)} data-testid="cs-zlecenie-pytanie-klient">
              Przygotuj pytanie do klienta
            </Button>
          )
        ) : null}
      </Box>
    </SectionCard>
  );
};
